/* eslint-disable react/prop-types */
import {
  useMemo, useState, useEffect, useCallback, useRef
} from 'react'
import styled from 'styled-components'
import {useDispatch} from 'redux-react-hook'
import {
  Box,
  FlexBetween,
  Dashboard,
  Text,
  Flex,
  Button,
  TextField,
  Notification,
  TextButton
} from '@binance/uikit'
import Tooltip from 'components/Tooltip'
import {updateOrderPrice, updateOrderQty, resetOrderForm} from 'actions'
import {formatNumber, formatDate} from 'utils/format'
import {floor} from 'utils/number'
import {positionROEValueFn} from 'utils/futures'
import useUserLoginStatus from 'hooks/useUserLoginStatus'
import useUserDataStreamer from 'hooks/useUserDataStreamer'
import usePositionData from 'hooks/usePositionData'
import useSymbolInfo from 'hooks/useSymbolInfo'
import useRouter from 'hooks/useRouter'
import useOpenOrders from 'hooks/useOpenOrders'
import tipMsgData from 'assets/tipMsgData'
import useOrderForm from 'hooks/useOrderForm'
import useOpenAccount from 'hooks/useOpenAccount'
import useLeverageData from 'hooks/useLeverageData'
import {
  TRADE_TYPES, TRADE_SIDES, TIME_IN_FORCE, SETTING
} from 'constants'
import {
  onInputChange, onKeyPress, onBlur, onKeyDown
} from 'utils/limitInput'
import ApiSdk from 'services/callApi'
import {getPowOf} from 'utils/common'
import {mediaJs} from 'utils/media'
import Table from './Table'
import {NoData} from './NoData'
import ConfirmDialog from '../OrderForm/ConfirmDialog'

const StyledFlexBetween = styled(FlexBetween)`
  margin: 0;
  > div {
    > div {
      margin: 0;
      > div {
        height: 24px;
      }
    }
  }
`

const getTitles = i18n => [
  {
    title: i18n['tradeHistory-symbolTitle'] || 'Symbol',
    key: 'symbol',
    width: 110
  },
  {
    title: i18n['tradeHistory-sizeTitle'] || 'Size',
    key: 'positionSize',
    direction: 'start',
    width: 110
  },
  {
    title: (
      <Tooltip
        label={i18n['tradeHistory-positions-entryPriceTitle'] || 'Entry Price'}
        tip={i18n['tradeHistory-positions-entryPriceTip'] || tipMsgData.entryPrice}
      />
    ),
    key: 'entryPrice',
    direction: 'start',
    width: 110
  },
  {
    title: (
      <Tooltip
        label={i18n['tradeHistory-positions-markPriceTitle'] || 'Mark Price'}
        tip={i18n['tradeHistory-positions-markPriceTip'] || tipMsgData.markPrice}
      />
    ),
    key: 'markPrice',
    direction: 'start',
    width: 100
  },
  {
    title: (
      <Tooltip
        label={i18n['tradeHistory-positions-liquidationPriceTitle'] || 'Liq. Price'}
        tip={i18n['tradeHistory-positions-liquidationPriceTip'] || tipMsgData.liquidationPrice}
      />
    ),
    key: 'liquidationPrice',
    direction: 'start',
    width: 110
  },
  {
    title: (
      <Tooltip
        label={i18n['tradeHistory-positions-unrealizedPNLShort'] || 'PNL（ROE %）'}
        tip={i18n['tradeHistory-positions-unrealizedPnlTip'] || tipMsgData.unrealizedPnl}
      />
    ),
    key: 'unrealizedPnl',
    direction: 'end',
    width: 110
  },
  {
    title: (
      <Tooltip
        label={i18n['tradeHistory-positions-closePositionTitle'] || 'Close Position'}
        tip={i18n['tradeHistory-positions-closePositionTip'] || tipMsgData.closePosition}
      />
    ),
    key: 'closePosition',
    width: 110
  }
]

const colorBlock = el => (
  <>
    <Box
      my="auto"
      mr={3}
      width={3}
      height={10}
      borderRadius={1}
      display="inline-block"
      style={{background: el.positionSize < 0 ? '#FF0372' : '#00c582'}}
    />
    {el.symbol}
  </>
)

const PositionPane = ({i18n}) => {
  const [hide, setHide] = useState(true)
  const [side, setSide] = useState(TRADE_SIDES.BUY)
  const [formType, setFormType] = useState(TRADE_TYPES.LIMIT)
  const [isEdit, setIsEdit] = useState(false)
  const [orderInfo, setOrderInfo] = useState()
  const [showDialog, setShowDialog] = useState(false)
  const [tradeStatus, setTradeStatus] = useState({})
  const router = useRouter()
  const {symbol, lng: lang} = router.query
  const isLogin = useUserLoginStatus()
  const [btnColor, setBtnColor] = useState('#848e9c')
  const dispatch = useDispatch()
  useUserDataStreamer(symbol)
  const {balanceData, positionList} = usePositionData(symbol)
  const {symbolInfo} = useSymbolInfo()
  const {quoteAsset, pricePrecision, maxLen} = symbolInfo
  const {openOrders, cancelOrder} = useOpenOrders(symbolInfo)
  const {isOpenAccount} = useOpenAccount(i18n, '')
  const {leverageData} = useLeverageData()
  const {curLeverage} = leverageData

  const lastSide = useRef(side)
  useEffect(() => {
    if (lastSide.current === side) return
    lastSide.current = side
  }, [side])
  const lastFormType = useRef(TRADE_TYPES.LIMIT)
  useEffect(() => {
    if (lastFormType.current === formType) return
    lastFormType.current = formType
  }, [formType])
  useEffect(() => {
    const timer = setTimeout(() => {
      clearTimeout(timer)
      // const orderInfo2 = JSON.parse(window.localStorage.getItem('closePosition_orderInfo'))
      if (
        orderInfo
        && openOrders
        && openOrders?.filter(v => Number(v.orderId) === Number(orderInfo?.orderId))?.length === 0
      ) {
        setOrderInfo()
      }
    }, 1e3)
  }, [openOrders, orderInfo])

  useEffect(() => {
    const [info] = positionList?.filter(v => (v.symbol = symbol))
    if (info && Number(info.positionSize) === 0) {
      setOrderInfo()
    }
  }, [positionList, symbol])

  const {buyPrice, sellPrice} = useOrderForm(symbol, quoteAsset, TRADE_TYPES.LIMIT)

  const handlePriceChange = useCallback(
    (buyOrSell, price) => {
      setIsEdit(true)
      dispatch(updateOrderPrice(buyOrSell, price, TRADE_TYPES.LIMIT))
    },
    [dispatch]
  )

  const resetForm = useCallback(
    buyOrSell => {
      dispatch(resetOrderForm(buyOrSell, TRADE_TYPES.LIMIT))
    },
    [dispatch]
  )

  const switchDialog = useCallback(() => {
    setShowDialog(!showDialog)
  }, [showDialog])

  const handleTrade = useCallback(
    (isConfirmed, {tradeType, tradeSide} = {}) => {
      tradeType = tradeType || lastFormType.current
      tradeSide = tradeSide || lastSide.current
      const isTrade = isConfirmed === true
        || JSON.parse(window.localStorage.getItem(`dialog_${tradeType}_${tradeSide}`))
      if (isTrade) {
        resetForm(tradeSide)
        const isBuy = tradeSide === TRADE_SIDES.BUY
        const isLimit = tradeType === TRADE_TYPES.LIMIT
        const dealTrade = isBuy ? ApiSdk.futures.placeBuyOrder : ApiSdk.futures.placeSellOrder
        const {markPrice, positionSize} = positionList.filter(v => v.symbol === symbol)[0]
        const price = isLimit ? (isBuy ? buyPrice : sellPrice) : markPrice
        const quantity = Math.abs(positionSize)
        const params = {
          symbol,
          quantity: Number(quantity),
          price: Number(price),
          type: tradeType,
          timeInForce: TIME_IN_FORCE.TIF.GTC,
          reduceOnly: SETTING.enableReduceOnly
        }
        dealTrade(params, {lang}).then(d => {
          setTradeStatus({...tradeStatus, [tradeType]: false})
          if (d?.order) {
            if (isLimit) {
              setOrderInfo(d.order)
              // window.localStorage.setItem('closePosition_orderInfo', d.order)
            }
            Notification({
              content: i18n[`${tradeType}Form-${tradeSide}Success`],
              type: 'success',
              duration: 3000
              // time: formatDate(new Date(), 'HH:mm:ss')
            })
          } else {
            Notification({
              content: d?.error?.message || i18n[`${tradeType}Form-${tradeSide}Failure`],
              type: 'error',
              duration: 3000
              // time: formatDate(new Date(), 'HH:mm:ss')
            })
          }
        })
      } else {
        setTradeStatus({...tradeStatus, [tradeType]: false})
        switchDialog()
      }
    },
    [buyPrice, i18n, lang, positionList, resetForm, sellPrice, switchDialog, symbol, tradeStatus]
  )

  const handleQtyChange = useCallback(
    (buyOrSell, qty, tradeType) => {
      setTradeStatus({...tradeStatus, [tradeType]: true})
      setFormType(tradeType)
      setSide(buyOrSell)
      dispatch(updateOrderQty(buyOrSell, Math.abs(qty), tradeType))
      handleTrade(false, {tradeType, tradeSide: buyOrSell})
    },
    [dispatch, handleTrade, tradeStatus]
  )

  const result = useMemo(
    () =>
      Object.values(positionList)
      .filter(el => el.positionSize && Number(el.positionSize) !== 0)
      .map(v => {
        const {
          positionSize, entryPrice, markPrice, unrealizedPnl, liquidationPrice
        } = v
        const tradeSide = positionSize > 0 ? TRADE_SIDES.SELL : TRADE_SIDES.BUY
        const isBuy = tradeSide === TRADE_SIDES.BUY
        const price = isBuy ? buyPrice : sellPrice
        if (!price && !isEdit && markPrice) {
          handlePriceChange(tradeSide, Number(markPrice).toFixed(2))
        }
        const handleLimitTrade = () => {
          const nPrice = Number(price)
          if (nPrice > 0) {
            handleQtyChange(tradeSide, positionSize, TRADE_TYPES.LIMIT)
          }
        }
        let ROEVal = positionROEValueFn({
          positionSize, entryPrice, markPrice, leverage: curLeverage
        })
        ROEVal = Number.isNaN(ROEVal) ? 0 : ROEVal

        return {
          ...v,
          symbol: colorBlock(v),
          entryPrice: formatNumber(entryPrice),
          markPrice: formatNumber(markPrice),
          unrealizedProfit: formatNumber(v.unrealizedProfit),
          liquidationPrice:
              Number(liquidationPrice) > 0 ? formatNumber(liquidationPrice || 0) : '-',
          unrealizedPnl: `${formatNumber(
            Number.isNaN(unrealizedPnl) ? 0 : unrealizedPnl
          )} (${ROEVal})`,
          closePosition: (
            <StyledFlexBetween
              before={
                orderInfo ? null : (
                  <TextField
                    type="text"
                    value={price}
                    step={getPowOf(pricePrecision)}
                    min="0"
                    onKeyPress={e => onKeyPress(e, maxLen)}
                    handleChange={e =>
                      onInputChange(e, handlePriceChange, tradeSide, pricePrecision)
                    }
                    onBlur={e => onBlur(e)}
                    onKeyDown={e => onKeyDown(e, handleLimitTrade)}
                    style={{width: '67px'}}
                    placeholder={i18n.priceTitle || 'Price'}
                  />
                )
              }
              text=""
              after={
                orderInfo ? (
                    <>
                      <Text.Base>
                        {i18n.closeOrderAt || 'Close order at'}{' '}
                        {formatNumber(orderInfo.price, pricePrecision)}
                      </Text.Base>
                      <TextButton
                        hover="#F0B90B44"
                        color="#F0B90B"
                        style={{marginTop: '-1px'}}
                        onClick={() => {
                          cancelOrder(i18n, lang, {orderId: orderInfo.orderId, symbol})
                          setOrderInfo()
                          setIsEdit(false)
                        }}
                      >
                        {i18n.cancelBtnText || 'Cancel'}
                      </TextButton>
                    </>
                ) : (
                    <>
                      <Button
                        bg="primary"
                        color="#212833"
                        height="24px"
                        variant="small"
                        disabled={!price}
                        onClick={handleLimitTrade}
                        disabled={tradeStatus[TRADE_TYPES.LIMIT]}
                      >
                        {i18n.limit || 'Limit'}
                      </Button>
                      <Button
                        bg="primary"
                        color="#212833"
                        height="24px"
                        variant="small"
                        onClick={() => {
                          handleQtyChange(tradeSide, v.positionSize, TRADE_TYPES.MARKET)
                        }}
                        disabled={tradeStatus[TRADE_TYPES.MARKET]}
                      >
                        {i18n.market || 'Market'}
                      </Button>
                    </>
                )
              }
            />
          )
        }
      }),
    [positionList, buyPrice, sellPrice, isEdit, curLeverage, orderInfo, pricePrecision, i18n, tradeStatus, handlePriceChange, handleQtyChange, maxLen, cancelOrder, lang, symbol]
  )

  useEffect(() => {
    const positionBoard = window.localStorage.getItem('position_board')
    const isShowBoard = positionBoard === null ? true : JSON.parse(positionBoard)
    setHide(isShowBoard)
  }, [])

  const {
    markLevel = '0',
    totalMaintenanceMargin = '0',
    totalMarginBalance = '0',
    totalUnrealizedPnl = '0',
    walletBalance = '0'
  } = balanceData

  let schedule: any = (markLevel || 0) * 100
  schedule = Number(schedule)
  const mobile = mediaJs('phone')

  return useMemo(
    () =>
      isLogin ? (
        <>
          {showDialog && (
            <ConfirmDialog
              i18n={i18n}
              visible={showDialog}
              type={formType}
              side={side}
              onClose={switchDialog}
              handleBuy={handleTrade}
              handleSell={handleTrade}
            />
          )}
          <Flex flexDirection={mobile ? 'column' : 'row'} width="100%">
            <Box
              position={mobile ? '' : 'relative'}
              borderRight="2px solid #1b2027"
              style={mobile ? {} : {width: hide ? '22px' : '227px', transition: 'width 0.5s'}}
              p={16}
            >
              {mobile ? null : (
                <div
                  onClick={() => {
                    const hideBoard = !hide
                    setHide(hideBoard)
                    window.localStorage.setItem('position_board', hideBoard)
                  }}
                  onMouseEnter={() => {
                    setBtnColor('#f0b90b')
                  }}
                  onMouseLeave={() => {
                    setBtnColor('#848e9c')
                  }}
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: 32,
                    height: 32,
                    background: '#252d38',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: hide ? 'rotateY(180deg)' : '',
                    cursor: 'pointer',
                    zIndex: 1
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0)">
                      <path
                        d="M5.25195 8.0111L12 1.40893L10.7608 6.78472e-07L3.29146 7.30788C2.90238 7.68855 2.90292 8.33802 3.29264 8.71797L10.762 16L11.9988 14.5888L5.25195 8.0111Z"
                        fill={btnColor}
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0">
                        <rect
                          width="16"
                          height="16"
                          fill="white"
                          transform="translate(16 16) rotate(-180)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
              )}
              {(!hide || mobile) && (
                <>
                  <Dashboard
                    prevTitle={i18n.low || 'LOW'}
                    nextTitle={i18n.high || 'HIGH'}
                    schedule={schedule || 0}
                  />
                  <FlexBetween
                    before={
                      <Tooltip
                        label={i18n['tradeHistory-positions-marginRatioLabel'] || 'Margin Ratio'}
                        tip={
                          i18n['tradeHistory-positions-marginRatioTip'] || tipMsgData.marginRatio
                        }
                      />
                    }
                    solt={
                      <Text.Base fontSize={3} color="#00C582">
                        {formatNumber(schedule) || 0}%
                      </Text.Base>
                    }
                    mb={3}
                  />
                  <FlexBetween
                    before={
                      <Tooltip
                        label={
                          i18n['tradeHistory-positions-walletBalanceLabel'] || 'Wallet Balance'
                        }
                        tip={
                          i18n['tradeHistory-positions-walletBalanceTip']
                          || tipMsgData.walletBalance
                        }
                      />
                    }
                    text={isOpenAccount ? floor(walletBalance || 0, 2, true) : formatNumber(0)}
                    after={quoteAsset}
                    mb={3}
                    height={16}
                  />
                  <FlexBetween
                    before={
                      <Tooltip
                        label={
                          i18n['tradeHistory-positions-unrealizedPNLLabel'] || 'Unrealized PNL'
                        }
                        tip={
                          i18n['tradeHistory-positions-unrealizedPnlTip']
                          || tipMsgData.unrealizedPnl
                        }
                      />
                    }
                    text={formatNumber(totalUnrealizedPnl || 0)}
                    after={quoteAsset}
                    mb={3}
                    height={16}
                  />
                  <FlexBetween
                    before={
                      <Tooltip
                        label={
                          i18n['tradeHistory-positions-marginBalanceLabel'] || 'Margin Balance'
                        }
                        tip={
                          i18n['tradeHistory-positions-marginBalanceTip']
                          || tipMsgData.marginBalance
                        }
                      />
                    }
                    text={isOpenAccount ? floor(totalMarginBalance || 0, 2, true) : formatNumber(0)}
                    after={quoteAsset}
                    mb={3}
                    height={16}
                  />
                  <FlexBetween
                    before={
                      <Tooltip
                        label={
                          i18n['tradeHistory-positions-maintenanceMarginLabel']
                          || 'Maintenance Margin'
                        }
                        tip={
                          i18n['tradeHistory-positions-maintenanceMarginTip']
                          || tipMsgData.maintenanceMargin
                        }
                      />
                    }
                    text={formatNumber(totalMaintenanceMargin || 0)}
                    after={quoteAsset}
                    height={16}
                  />
                </>
              )}
            </Box>
            <Box flex={1}>
              <Table
                title={getTitles(i18n)}
                data={isLogin ? result : []}
                nodata={isLogin ? NoData('position') : null}
                style={{willChange: 'unset'}}
              />
            </Box>
          </Flex>
        </>
      ) : null,
    [
      isLogin,
      showDialog,
      i18n,
      formType,
      side,
      switchDialog,
      handleTrade,
      mobile,
      hide,
      btnColor,
      schedule,
      isOpenAccount,
      walletBalance,
      quoteAsset,
      totalUnrealizedPnl,
      totalMarginBalance,
      totalMaintenanceMargin,
      result
    ]
  )
}

export default PositionPane
